import PowerCalendarDays from 'ember-power-calendar/components/power-calendar/days';
import { isEmpty } from '@ember/utils';
import {
  add,
  endOf,
  endOfWeek,
  formatDate,
  isBefore,
  isSame,
  normalizeCalendarDay,
  startOf,
  startOfWeek,
} from 'ember-power-calendar/utils';
import moment from 'moment';

export default class AttendanceCalendarDays extends PowerCalendarDays {
  // term is the term object for the school term to be displayed.
  get term() {
    return this.args.term ?? null;
  }

  // showSaturday determines whether Saturdays are shown in the calendar.
  get showSaturday() {
    return this.args.showSaturday ?? false;
  }

  // showSunday determines whether Sundays are shown in the calendar.
  get showSunday() {
    return this.args.showSunday ?? false;
  }

  // isMini determines whether the calendar is of a mini size
  get isMini() {
    return this.args.isMini ?? false;
  }

  // shouldShowDay determines whether the given day of the week should be shown.
  shouldShowDay(day) {
    switch (day) {
      case 0:
        return this.showSunday;
      case 6:
        return this.showSaturday;
    }
    return true;
  }

  // weekdaysNames strips Saturday and/or Sunday out of the weekday names if
  // they're not being shown.
  get weekdaysNames() {
    let localeStartOfWeek = this.localeStartOfWeek;
    let format = '';
    switch (this.weekdayFormat) {
      case 'long':
        break;
      case 'min':
        format = 'Min';
        break;
      default:
        format = 'Short';
    }
    let weekdaysNames = this[`weekdays${format}`];
    return weekdaysNames
      .filter((_, index) => this.shouldShowDay(index))
      .slice(localeStartOfWeek)
      .concat(weekdaysNames.slice(0, localeStartOfWeek));
  }

  // days provides a list of days which should be shown in the attendance
  // calendar, excluding weekends if they're not needed.
  get days() {
    let today = this.powerCalendar.getDate();
    let calendar = this.args.calendar;
    let lastDay = this.lastDay();
    let day = this.firstDay();
    let days = [];
    while (isBefore(day, lastDay)) {
      if (this.shouldShowDay(day.getDay())) {
        days.push(this.buildDay(day, today, calendar));
      }
      day = add(day, 1, 'day');
    }
    // Add last day (unless it's a non-shown Saturday or Sunday).
    if (this.shouldShowDay(day.getDay())) {
      days.push(this.buildDay(day, today, calendar));
    }
    return days;
  }

  // buildDay builds the day object consumed by the template. Inlined here
  // because ember-power-calendar's own buildDay is a private, unexported
  // helper as of the Glimmer rewrite (0.20+) -- it's no longer an overridable
  // method on the base component.
  buildDay(date, today, calendar) {
    let id = formatDate(date, 'YYYY-MM-DD');
    return normalizeCalendarDay({
      id,
      number: date.getDate(),
      date: new Date(date),
      isDisabled: false,
      isFocused: this.focusedId === id,
      isCurrentMonth: isSame(date, this.currentCenter, 'month'),
      isToday: isSame(date, today, 'day'),
      isSelected: this.dayIsSelected(date, calendar),
    });
  }

  // firstDay determines the first day to show in the attendance calendar, based
  // off the term if one is specified.
  firstDay() {
    if (isEmpty(this.term)) {
      return startOfWeek(
        startOf(this.currentCenter, 'month'),
        this.localeStartOfWeek,
      );
    }
    return startOfWeek(
      moment(this.term.start, 'YYYY-MM-DD').toDate(),
      this.localeStartOfWeek,
    );
  }

  // lastDay determines the last day to show in the attendance calendar, based
  // off the term if one is specified.
  lastDay() {
    if (isEmpty(this.term)) {
      return endOfWeek(
        endOf(this.currentCenter, 'month'),
        this.localeStartOfWeek,
      );
    }
    return endOfWeek(
      moment(this.term.end, 'YYYY-MM-DD').toDate(),
      this.localeStartOfWeek,
    );
  }

  // weeks is adjusted to account for however many weekend days are shown.
  get weeks() {
    let { showDaysAround, days } = this;
    let weeks = [];
    let i = 0;
    let numPerWeek = 7;
    if (!this.showSaturday) {
      numPerWeek--;
    }
    if (!this.showSunday) {
      numPerWeek--;
    }
    while (days[i]) {
      let daysOfWeek = days.slice(i, i + numPerWeek);
      if (!showDaysAround) {
        daysOfWeek = daysOfWeek.filter((d) => d.isCurrentMonth);
      }
      weeks.push({
        id: `week-of-${daysOfWeek[0].id}`,
        days: daysOfWeek,
        missingDays: numPerWeek - daysOfWeek.length,
      });
      i += numPerWeek;
    }
    return weeks;
  }
}
